import { useWorkoutsWithMeta } from './use-workouts'
import { WorkoutSort } from '../context/filter-context'
import { Workout } from '../dao/core-dao'
import { WorkoutFilter } from '../service/core-service'
import { useEffect, useState } from 'react'

/** Fixed page size for the Results grid and section pages: 9 rows × 3 columns (GH-754). */
export const RESULTS_PAGE_SIZE = 27

/** Page setter; `replace` swaps the history entry (used for auto-correcting an out-of-range page). */
export type SetPage = (page: number, options?: { replace?: boolean }) => void

export interface PaginatedWorkouts {
  workouts: Workout[]
  totalSize: number
  totalPages: number
  page: number
  setPage: SetPage
  isLoading: boolean
}

/**
 * Externally-controlled pagination (e.g. the URL-backed filter context). `page` and `setPage` must be
 * supplied together — a controlled page with no setter would silently drop page changes.
 */
export type PaginatedWorkoutsOptions = { pageSize?: number } & (
  | { page: number; setPage: SetPage }
  | { page?: undefined; setPage?: undefined }
)

/**
 * Shared query/pagination logic for workout listings. Applies sort + paging to a base filter, exposes
 * the pagination meta, scrolls to the top on every re-query, and auto-corrects an out-of-range page
 * (e.g. a stale/shared `?page=99` URL) down to the last page. Pass `undefined` for `baseFilter` to hold
 * the query (e.g. while Express duration ids load).
 *
 * Pagination has two modes:
 * - **Controlled** (`options.page`/`setPage` provided): the caller owns the page — for the Workouts
 *   Results view and section pages, that's the URL via the filter context. Page resets on filter/sort
 *   change happen where the URL is written.
 * - **Uncontrolled** (no `options.page`): the hook keeps page in local state and resets to page 1 when
 *   the base filter or sort changes — used by the standalone listing screens (Flex Flow, Core Charge…).
 */
export const usePaginatedWorkouts = (
  baseFilter: WorkoutFilter | undefined,
  sort: WorkoutSort,
  options: PaginatedWorkoutsOptions = {},
): PaginatedWorkouts => {
  const { pageSize = RESULTS_PAGE_SIZE } = options
  const isControlled = options.page !== undefined

  const [internalPage, setInternalPage] = useState(1)
  const page = isControlled ? options.page : internalPage
  const setPage: SetPage = isControlled ? options.setPage : setInternalPage

  // Uncontrolled only: reset to page 1 whenever the base filter or sort changes (adjust state during
  // render against the previous inputs — no effect-based sync). Controlled callers reset via the URL.
  const resetKey = JSON.stringify({ baseFilter: baseFilter ?? null, sort })
  const [prevResetKey, setPrevResetKey] = useState(resetKey)
  if (!isControlled && prevResetKey !== resetKey) {
    setPrevResetKey(resetKey)
    setInternalPage(1)
  }

  const filter: WorkoutFilter | undefined = baseFilter
    ? { ...baseFilter, orderField: sort.orderField, orderDirection: sort.orderDirection, page, pageSize }
    : undefined
  const { data, isLoading } = useWorkoutsWithMeta(filter, { enabled: !!baseFilter })

  // Scroll back to the top on any re-query — filter/sort change (also resets to page 1) or a page click.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [resetKey, page])

  const workouts = data?.data ?? []
  const totalSize = data?.meta?.totalSize ?? workouts.length
  const totalPages = data?.meta?.totalPages ?? Math.ceil(totalSize / pageSize)

  // Auto-correct an out-of-range page (stale/shared URL, or a shrunken result set) to the last page,
  // replacing history so Back doesn't bounce onto the invalid page. Only once meta is known.
  useEffect(() => {
    if (!isLoading && totalPages >= 1 && page > totalPages) {
      setPage(totalPages, { replace: true })
    }
  }, [isLoading, totalPages, page, setPage])

  return { workouts, totalSize, totalPages, page, setPage, isLoading }
}
